<template>
    <div
        class="group flex items-stretch"
        :class="[wrapperClassName, attrs.class]"
        :style="attrs.style"
    >
        <div
            class="text-gray-500 flex items-stretch bg-transparent rounded-l transition-colors duration-150 group-focus-within:bg-white"
            data-testid="input-before-section"
            v-if="contentBefore"
        >
            <slot name="before">
                <span class="self-center ml-3" v-text="before" />
            </slot>
        </div>
        <div class="relative grow">
            <input
                :id="id"
                v-bind="inputAttrs"
                class="form-input"
                :class="internalInputClassName"
                :disabled="disabled"
                :readonly="readonly"
                :type="type"
                :value="parsedValue"
                ref="input"
                v-mask="maskOptions"
            />
        </div>
        <div
            class="text-gray-500 flex items-stretch bg-transparent rounded-r transition-colors duration-150 group-focus-within:bg-white"
            v-if="contentAfter"
        >
            <div class="self-center relative h-4 w-4 mr-3" v-tooltip="errorMessage" v-if="error">
                <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-50"
                />
                <alert-circle class="h-4 w-4 text-red-500" />
            </div>
            <spinner class="self-center h-4 w-4 mr-3" v-else-if="loading" />
            <slot name="after" v-else>
                <span class="self-center mr-3" v-text="after" />
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch, nextTick, inject, useSlots, useAttrs } from 'vue';
    import insertTextAtCursor from 'insert-text-at-cursor';
    // @ts-expect-error - No types for vue-imask
    import { IMaskDirective, IMask } from 'vue-imask';
    import AlertCircle from '@/components/activix-ui/assets/alert-circle.svg';
    import Spinner from '@/components/activix-ui/ActivixSpinner/ActivixSpinner.vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import { hasSlotContent } from '@/utils/vue-related.ts';

    defineOptions({ inheritAttrs: false, compatConfig: { MODE: 3 } });

    interface MaskedInputElement extends HTMLInputElement {
        maskRef?: {
            value: string;
            unmaskedValue: string;
        };
    }

    // Custom directive
    const vMask = IMaskDirective;

    // Props interface
    interface Props {
        allowNegativeValue?: boolean;
        after?: string;
        before?: string;
        disabled?: boolean;
        error?: boolean;
        errorMessage?: string;
        highlighted?: boolean;
        boldHighlighted?: boolean;
        id?: string;
        inputClassName?: string | string[] | Record<string, boolean>;
        invisible?: boolean;
        loading?: boolean;
        mask?: string | string[];
        maskMoneySymbol?: string;
        maskRadix?: string;
        maskScale?: number | null;
        maskThousandsSeparator?: string;
        maskUpperCase?: boolean;
        min?: number;
        max?: number;
        modelValue?: string | number | null;
        paddingClassName?: string | string[] | Record<string, boolean>;
        readonly?: boolean;
        type?: string;
        value?: string | number | null;
    }

    // Props with defaults
    const props = withDefaults(defineProps<Props>(), {
        allowNegativeValue: false,
        after: '',
        before: '',
        disabled: false,
        error: false,
        errorMessage: '',
        highlighted: false,
        boldHighlighted: false,
        id: '',
        inputClassName: '',
        invisible: false,
        loading: false,
        mask: '',
        maskMoneySymbol: '$',
        maskRadix: '',
        maskScale: null,
        maskThousandsSeparator: '',
        maskUpperCase: false,
        min: -2147483647, // PostgreSQL minimum
        max: 2147483647, // PostgreSQL maximum
        modelValue: undefined,
        paddingClassName: 'py-2 px-3',
        readonly: false,
        type: 'text',
        value: '',
    });

    // FIXME(VUE3): Remove legacy 'input' event and 'value' prop after consumers migrate to v-model
    const emit = defineEmits<{
        (e: 'input', value: string | number): void;
        (e: 'update:modelValue', value: string | number): void;
        (e: 'focus'): void;
        (e: 'blur'): void;
    }>();

    // Injections and hooks
    const { isFrLocale } = useI18n();
    const oldFontScale = inject<boolean>('oldFontScale', true);
    const slots = useSlots();
    const attrs = useAttrs();

    // Refs
    const isFocused = ref(false);
    const input = ref<HTMLInputElement>();
    const cachedNumericMask = ref<ReturnType<typeof IMask.createMask> | null>(null);
    const cachedMaskOptionsJson = ref<string>('');

    // Computed properties
    const isFloat = computed(() => {
        return ['decimal', 'money', 'percent'].includes(props.mask as string);
    });

    const isInteger = computed(() => {
        return ['integer', 'km', 'mi'].includes(props.mask as string);
    });

    const resolvedValue = computed(() => {
        return props.modelValue === undefined ? props.value : props.modelValue;
    });

    const parsedValue = computed(() => {
        if (resolvedValue.value == null || resolvedValue.value === '') {
            return '';
        }

        const str = resolvedValue.value.toString();

        if (!isFloat.value) {
            return str;
        }

        // When using a custom radix (e.g. ',' for European formatting), replace
        // JavaScript's default '.' decimal separator with the configured radix so
        // IMask doesn't misinterpret it as the thousands separator.
        const radix = maskNumericOptions.value.radix;
        if (radix && radix !== '.' && str.includes('.')) {
            return str.replace('.', radix);
        }

        return str;
    });

    const hasValue = computed(() => parsedValue.value.length > 0);

    const contentBefore = computed(() => {
        return !!props.before || hasSlotContent(slots, 'before');
    });

    const contentAfter = computed(() => {
        return !!props.after || props.error || props.loading || hasSlotContent(slots, 'after');
    });

    const hashDefinition = computed(() => {
        switch (props.mask) {
            case 'phone':
            case 'phoneWithCountry':
                return /[2-9]/;
            default:
                return /[1-9]/;
        }
    });

    const maskPattern = computed(() => {
        switch (props.mask) {
            case 'phone':
                return '#00 000-0000';
            case 'phoneWithCountry':
                return '{+1} #00 000-0000';
            case 'postalCode':
                return 'a0a 0a0';
            case 'zipCode':
                return '00000[-0000]';
            default:
                return props.mask;
        }
    });

    const maskNumericOptions = computed(() => {
        const defaultRadix = isFrLocale.value ? ',' : '.';
        const defaultThousandsSeparator = isFrLocale.value ? ' ' : ',';

        return {
            lazy: true,
            mask: Number,
            signed: true,
            max: props.max,
            min: props.allowNegativeValue ? props.min : 0,
            scale: props.maskScale || 2,
            radix: props.maskRadix || defaultRadix,
            mapToRadix: ['.', ','],
            thousandsSeparator: props.maskThousandsSeparator || defaultThousandsSeparator,
            padFractionalZeros: true,
        };
    });

    const maskOptions = computed(() => {
        if (!props.mask) {
            return null;
        }

        switch (props.mask) {
            case 'integer':
                return {
                    ...maskNumericOptions.value,
                    scale: 0,
                };

            case 'decimal':
                return maskNumericOptions.value;

            case 'money':
                return {
                    eager: true,
                    mask: isFrLocale.value
                        ? `num ${props.maskMoneySymbol}`
                        : `${props.maskMoneySymbol}num`,
                    blocks: {
                        num: {
                            ...maskNumericOptions.value,
                            scale: 2,
                        },
                    },
                };

            case 'percent':
                return {
                    eager: true,
                    mask: 'num %',
                    blocks: {
                        num: {
                            ...maskNumericOptions.value,
                            max: 100,
                        },
                    },
                };

            case 'km':
            case 'mi':
                return {
                    eager: true,
                    mask: `num ${props.mask}`,
                    blocks: {
                        num: {
                            ...maskNumericOptions.value,
                            scale: 0,
                        },
                    },
                };

            default:
                return {
                    lazy: true,
                    mask: maskPattern.value,
                    prepare: (value: string) => {
                        return props.maskUpperCase || props.mask === 'postalCode'
                            ? value.toLocaleUpperCase()
                            : value;
                    },
                    definitions: {
                        '#': hashDefinition.value,
                    },
                };
        }
    });

    const wrapperClassName = computed(() => {
        const focusClassName = props.error
            ? 'ring-1 ring-red-500 border-red-500'
            : 'ring-1 ring-blue-500 !border-blue-500';

        return [
            'border rounded overflow-hidden',
            'transition-colors duration-150',
            isFocused.value ? focusClassName : 'border-gray-400',
            {
                'border-opacity-0': props.invisible && !isFocused.value,
                'hover:border-opacity-100 focus-within:border-opacity-100': !props.disabled,
                'border-b-blue-500 ':
                    !props.disabled && !props.invisible && props.highlighted && hasValue.value,
                'bg-blue-500':
                    !props.disabled && !props.invisible && props.boldHighlighted && hasValue.value,
            },
        ];
    });

    const internalInputClassName = computed(() => {
        return [
            'block min-w-0 w-full border-0 rounded placeholder-gray-800 placeholder-opacity-30',
            'focus:outline-none focus:ring-0 focus:border-transparent',
            'transition-colors duration-150',
            props.paddingClassName,
            props.inputClassName,
            {
                'sm:text-sm': !oldFontScale,
                'bg-transparent': props.invisible,
                'bg-gray-100': props.disabled && !props.invisible,
                'cursor-not-allowed': props.disabled,
                'bg-blue-500 text-white':
                    !props.disabled && !props.invisible && props.boldHighlighted && hasValue.value,
            },
        ];
    });

    // Watch - reformat display when locale changes (without emitting input event)
    watch(
        () => isFrLocale.value,
        async () => {
            if (!parsedValue.value || !props.mask) {
                return;
            }

            const value = parsedValue.value;

            await nextTick();

            const masked = IMask.createMask(maskOptions.value);
            masked.resolve(value);

            if (input.value) {
                input.value.value = masked.value;
            }
        }
    );

    // Methods
    const focus = async () => {
        await nextTick();
        input.value?.focus();
    };

    const blur = () => {
        input.value?.blur();
    };

    const insertAtCaret = async (text: string) => {
        await nextTick();
        if (input.value) {
            insertTextAtCursor(input.value, text);
        }
    };

    const getOrCreateNumericMask = () => {
        const currentOptionsJson = JSON.stringify(maskOptions.value);
        if (!cachedNumericMask.value || cachedMaskOptionsJson.value !== currentOptionsJson) {
            cachedNumericMask.value = IMask.createMask(maskOptions.value);
            cachedMaskOptionsJson.value = currentOptionsJson;
        }
        return cachedNumericMask.value;
    };

    const updateNumericValue = (event: any) => {
        if (!isFloat.value && !isInteger.value) {
            return;
        }

        const masked = getOrCreateNumericMask();
        masked.resolve(event.target.value);

        if (event.data === '0' || masked.unmaskedValue !== '0') {
            return;
        }

        if (!event.data) {
            return;
        }

        masked.resolve(event.data);

        if (masked.unmaskedValue && input.value) {
            input.value.value = masked.value;
        }
    };

    const onFocus = () => {
        isFocused.value = true;
        emit('focus');
    };

    const onBlur = () => {
        isFocused.value = false;
        emit('blur');
    };

    const onInput = (event: any) => {
        event.stopPropagation();
        updateNumericValue(event);

        if (!props.mask) {
            update(event.target.value);
        }
    };

    function normalizePhoneWithCountryPaste(pasted: string): string | null {
        const digits = pasted.replaceAll(/\D/g, '');
        if (!digits) return null;

        const hasCountryPrefix =
            (pasted.includes('+') || digits.length > 10) && digits.startsWith('1');
        const national = hasCountryPrefix ? digits.slice(1) : digits;

        return `+1${national.slice(0, 10)}`;
    }

    const onPaste = (event: ClipboardEvent) => {
        const pasted = event.clipboardData?.getData('text');
        if (!pasted) return;

        let normalized: string | null = null;

        switch (props.mask) {
            case 'phoneWithCountry':
                normalized = normalizePhoneWithCountryPaste(pasted);
                break;

            default:
                return;
        }

        event.preventDefault();

        if (!normalized) return;

        const maskedInput = input.value as MaskedInputElement | undefined;
        if (maskedInput?.maskRef) {
            maskedInput.maskRef.unmaskedValue = normalized;
            maskedInput.value = maskedInput.maskRef.value;
        }
    };

    const onAccept = (event: any) => {
        if (props.mask) {
            update(event.detail.unmaskedValue);
        }
    };

    const inputAttrs = computed(() => {
        // Strip class/style — they belong on the wrapper <div>, not the <input>.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { class: _cls, style: _sty, ...rest } = attrs;
        return { ...rest, onAccept, onBlur, onFocus, onInput, onPaste };
    });

    const update = (value: string) => {
        if (value === '' || value == null) {
            if (resolvedValue.value !== '' && resolvedValue.value != null) {
                emit('input', value ?? '');
                emit('update:modelValue', value ?? '');
            }
            return;
        }

        let formattedValue: string | number;

        if (isFloat.value) {
            formattedValue = Number.parseFloat(value);
            if (Number.isNaN(formattedValue)) {
                return;
            }
        } else if (isInteger.value) {
            formattedValue = Number.parseInt(value, 10);
            if (Number.isNaN(formattedValue)) {
                return;
            }
        } else {
            formattedValue = value;
        }

        // Don't emit if value hasn't actually changed (e.g., locale-triggered reformat)
        if (formattedValue === resolvedValue.value) {
            return;
        }

        emit('input', formattedValue);
        emit('update:modelValue', formattedValue);
    };

    // Expose public methods
    defineExpose({
        focus,
        blur,
        insertAtCaret,
    });
</script>
